
import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Box,
  CardActions,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getApiBaseUrl, getAppBaseUrl } from '../utils/baseUrl';
import { useLoading, acquireTokenSafe } from '../App';

const apiUrl = getApiBaseUrl();

interface JobStatus {
  name: string;
  description: string;
  schedule: string;
  lastRun?: string;
  status: 'success' | 'error' | 'running' | 'unknown';
  endpoint: string;
  errorMessage?: string;
}

const LegacyScheduledJobs = () => {
  const { instance } = useMsal();
  const { setLoading } = useLoading();
  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const [runningJobs, setRunningJobs] = useState<Set<string>>(new Set());

  // Static job definitions based on server.js
  const jobDefinitions: Omit<JobStatus, 'lastRun' | 'status'>[] = [
    {
      name: "CRO Date Validation",
      description: "Validates company dates between Senta and CRO registry",
      schedule: "Daily at 1:00 AM UTC",
      endpoint: "/api/cro/validate-dates",
    },
    {
      name: "CRO Data Dump",
      description: "Fetches and stores all CRO company data for active clients",
      schedule: "Daily at 4:00 AM UTC",
      endpoint: "/api/cro/data-dump",
    },
    {
      name: "Weekly Timekeeping Report",
      description: "Generates weekly timekeeping reports from MinuteDock data",
      schedule: "Monday at 2:00 AM UTC",
      endpoint: "/api/timekeeping/generate-weekly-report",
    },
    {
      name: "Weekly Work Activity Report",
      description: "Generates weekly work activity breakdown reports",
      schedule: "Monday at 2:30 AM UTC",
      endpoint: "/api/work-activity/generate-weekly-report",
    },
  ];

  const fetchJobStatuses = async () => {
    setLoading(true);
    try {
      const accounts = instance.getAllAccounts();
      const token = await acquireTokenSafe(instance, accounts);
      if (!token) { setLoading(false); return; }
      const authHeader = `Bearer ${token}`;

      // For now, we'll set all jobs as unknown status since we don't have a status endpoint
      // In a real implementation, you'd want to track job execution status
      const jobStatuses: JobStatus[] = jobDefinitions.map(job => ({
        ...job,
        status: 'unknown' as const,
        lastRun: undefined,
      }));

      setJobs(jobStatuses);
    } catch (err) {
      console.error("Error fetching job statuses:", err);
      setSnackbarMessage("Failed to fetch job statuses");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const runJob = async (job: JobStatus) => {
    const jobName = job.name;
    setRunningJobs(prev => new Set(prev).add(jobName));

    try {
      const accounts = instance.getAllAccounts();
      const token = await acquireTokenSafe(instance, accounts);
      if (!token) throw new Error('No account found');
      const authHeader = `Bearer ${token}`;

      const response = await fetch(`${apiUrl}${job.endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSnackbarMessage(data.message || `${jobName} triggered successfully`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      // Update job status
      setJobs(prevJobs => 
        prevJobs.map(j => 
          j.name === jobName 
            ? { ...j, status: 'success' as const, lastRun: new Date().toISOString() }
            : j
        )
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to run ${jobName}`;
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

      // Update job status with error
      setJobs(prevJobs => 
        prevJobs.map(j => 
          j.name === jobName 
            ? { ...j, status: 'error' as const, errorMessage, lastRun: new Date().toISOString() }
            : j
        )
      );
    } finally {
      setRunningJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobName);
        return newSet;
      });
    }
  };

  const getStatusIcon = (status: JobStatus['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon sx={{ color: '#4caf50' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#f44336' }} />;
      case 'running':
        return <CircularProgress size={24} />;
      default:
        return <ScheduleIcon sx={{ color: '#9e9e9e' }} />;
    }
  };

  const getStatusColor = (status: JobStatus['status']) => {
    switch (status) {
      case 'success':
        return '#e8f5e8';
      case 'error':
        return '#ffebee';
      case 'running':
        return '#e3f2fd';
      default:
        return '#f5f5f5';
    }
  };

  const formatLastRun = (lastRun?: string) => {
    if (!lastRun) return 'Never';
    return new Date(lastRun).toLocaleString();
  };

  useEffect(() => {
    fetchJobStatuses();
  }, [instance]);

  return (
    <div style={{ padding: 20, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Typography variant="h1" gutterBottom>
        Legacy Scheduled Jobs
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchJobStatuses}
          sx={{ mr: 2 }}
        >
          Refresh Status
        </Button>
      </Box>

      <Grid container spacing={3}>
        {jobs.map((job) => (
          <Grid key={job.name} size={{ xs: 12, md: 6, lg: 4 }}>
            <Card elevation={3}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getStatusIcon(runningJobs.has(job.name) ? 'running' : job.status)}
                  <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                    {job.name}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {job.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={job.schedule}
                    size="small"
                    sx={{ mb: 1, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                  />
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Last Run:</strong> {formatLastRun(job.lastRun)}
                  </Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Status:</strong>
                    <Chip
                      label={runningJobs.has(job.name) ? 'Running' : job.status}
                      size="small"
                      sx={{ 
                        ml: 1, 
                        backgroundColor: getStatusColor(runningJobs.has(job.name) ? 'running' : job.status),
                        textTransform: 'capitalize'
                      }}
                    />
                  </Typography>
                </Box>

                {job.errorMessage && (
                  <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {job.errorMessage}
                  </Alert>
                )}
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  startIcon={runningJobs.has(job.name) ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  onClick={() => runJob(job)}
                  disabled={runningJobs.has(job.name)}
                  variant="contained"
                  sx={{
                    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                    '&:disabled': {
                      background: '#ccc'
                    }
                  }}
                >
                  {runningJobs.has(job.name) ? 'Running...' : 'Run Now'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LegacyScheduledJobs;
